import { useMemo, useState } from 'react'
import type { FC } from 'react'
import clsx from 'clsx'
import { useDelay } from '~/utils/hooks'
import {
	RoundedTextInput,
	SelectDropdown,
	InlineMultiChoice,
	MultiChoiceDisplayValue,
	Conversation,
	ConversationQuestion,
	formParams,
	PrimaryButton,
	ErrorText,
} from '@alexanderathoodly/ui-library'
import {
	useForm,
	EmailField,
	PhoneNumberField,
	ListField,
	MappingField,
	StringField,
	useSteps,
	NumberField,
} from '@alexanderathoodly/formify'
import { createOrUpdateFormLead } from '../actions'
import { getPersonOfTheDay } from '~/utils/teams'
import Shape from '#/a-shapes/shape4.svg'
import { getGAClientId } from '~/forms/utils'
import { GAAction, sendGA4Event } from '~/analytics/ga'
import { useHistory, useLocation } from 'react-router-dom'
import type { FormLeadPayload } from '@alexanderathoodly/data-models'
import {
	StatusError,
	useYtaQuery,
	ytaUrls,
} from '@alexanderathoodly/data-models'
import { Fade } from '@chakra-ui/react'
import { useQuery } from 'react-query'
import { getCoworkingSearchResultQuery } from '~/queries/coworking_search_result'
import {
	OFFICE_TYPE,
	SORT_RECOMMENDED,
	useSerpUrlGenerator,
	FILTER_TYPE_OFFICE,
} from '~/routes/coworking_serp/urlGenerator'
import MinWorkspacesSection from './MinWorkspacesSection'
import { useTranslation } from 'react-i18next'

type MappedAreas = {
	[city: string]: { [area: string]: string[] }
}

/**
 * As these cities have a smaller supply we don't want to filter on areas when they go to the SERP
 * after completing KH or if they are 1-2ws and we suggest they search themselves.
 */
const UPPSALA_FILTER_AREA = ['Uppsala']
const HELSINGBORG_FILTER_AREA = ['Helsingborg']

const AREAS_MAPPING: MappedAreas = {
	Stockholm: {
		'Innanför tullarna': [
			'Gamla stan',
			'Södermalm',
			'Kungsholmen',
			'Vasastan',
			'City',
			'Östermalm',
		],
		'Stockholm City': ['City'],
		Vasastan: ['Vasastan'],
		Östermalm: ['Östermalm'],
		Södermalm: ['Södermalm'],
		Kungsholmen: ['Kungsholmen'],
		Kista: ['Kista'],
		Sundbyberg: ['Sundbyberg'],
		Solna: ['Solna'],
		Övrigt: [
			'Liljeholmen',
			'Hammarby Sjöstad',
			'Bromma',
			'Sollentuna',
			'Danderyd',
			'Täby',
			'Lidingö',
			'Globen',
			'Nacka',
			'Upplands Väsby',
			'Söderort',
			'Arlanda Märsta',
		],
	},
	Göteborg: {
		Nordost: ['Nordost'],
		City: ['City'],
		Lindholmen: ['Lindholmen'],
		Mölndal: ['Mölndal'],
	},
	Malmö: {
		City: ['Malmö City'],
		Hyllie: ['Hyllie'],
		Fosie: ['Fosie'],
	},
	Uppsala: {
		Centrum: UPPSALA_FILTER_AREA,
		Kungsängen: UPPSALA_FILTER_AREA,
		Boländerna: UPPSALA_FILTER_AREA,
		Fyrislund: UPPSALA_FILTER_AREA,
		Librobäck: UPPSALA_FILTER_AREA,
	},
	Helsingborg: {
		Söder: HELSINGBORG_FILTER_AREA,
		Centrum: HELSINGBORG_FILTER_AREA,
	},
}

const SIZE_SEGMENTS = {
	'1-2': { min: 1, max: 2 },
	'3-5': { min: 3, max: 5 },
	'6-10': { min: 6, max: 10 },
	'11-20': { min: 11, max: 20 },
	'21-50': { min: 21, max: 50 },
	'50+': { min: 50, max: null },
}

const SIZE_SEGMENT_CHOICES = Object.keys(SIZE_SEGMENTS).map((v) => ({
	display: v,
	value: v,
}))

const SIZE_SEGMENTS_MAPPING = { '': null, ...SIZE_SEGMENTS }

const CITY_CHOICES = Object.keys(AREAS_MAPPING).map((s) => ({
	display: s,
	value: s,
}))

const AREA_CHOICES = Object.keys(AREAS_MAPPING).reduce(
	(
		acc: { [city: string]: Array<{ display: string; value: string }> },
		city: string
	) => {
		acc[city] = Object.keys(AREAS_MAPPING[city]!).map((s) => ({
			display: s,
			value: s,
		}))
		return acc
	},
	{}
)

const ANSWER_POST_TEXTS = {
	size: 'arbetsplatser',
	budget: 'SEK/månad för vårt kontor',
} // To handle TypeScript warning

const useYtaUrls = () => {
	const { data, isLoading } = useYtaQuery(ytaUrls())
	return useMemo(() => {
		if (isLoading || !data) {
			return undefined
		}
		return Object.keys(AREAS_MAPPING).reduce(
			(
				accum: {
					[city: string]: { name: string; id: string }[]
				},
				city
			) => {
				accum[city] = data
					.filter((area) => area.url.includes(city.toLowerCase()))
					.map((area) => {
						return { name: area.name, id: `${area.type}${area.id}` }
					})
				return accum
			},
			{}
		)
	}, [data, isLoading])
}

function useMappedAreas(
	ytaUrls:
		| {
				[city: string]: {
					name: string
					id: string
				}[]
		  }
		| undefined
) {
	if (!ytaUrls) {
		return undefined
	}
	const mappedAreasObj: MappedAreas = {}
	Object.keys(AREAS_MAPPING).forEach((city) => {
		const cityObj = AREAS_MAPPING[city]!
		mappedAreasObj[city] = {}
		Object.keys(cityObj).forEach((area) => {
			const areaGroup = cityObj[area]!
			const areaIds = areaGroup
				.map((area) => ytaUrls[city]!.find((url) => url.name == area)?.id)
				.filter((areaId): areaId is string => !!areaId)
			if (areaIds.length > 0) {
				mappedAreasObj[city]![area] = areaIds
			}
		})
	})
	return mappedAreasObj
}

const FormSection: FC = () => {
	const { t } = useTranslation()
	const delay = useDelay(1500)
	const manager = getPersonOfTheDay()
	const [leadID, setLeadID] = useState<string | null>(null)
	const history = useHistory()
	const { pathname } = useLocation()
	const ytaUrls = useYtaUrls()
	const mappedAreas = useMappedAreas(ytaUrls)
	const mapping = {
		fromForm: async (formData: FormData) => {
			const gaClientId = await getGAClientId()
			return {
				submitted: false,
				ga_client_id: gaClientId || '',
				...formData,
			}
		},
		toForm: (data: FormLeadPayload) => {
			return {
				size_segment: data.size_segment,
				areas: data.areas,
				monthly_budget: data.monthly_budget,
				company: data.company,
				email: data.email,
				phone_number: data.phone_number,
				city: data.city,
			}
		},
	}
	const form = useForm({
		fields: {
			size_segment: MappingField(SIZE_SEGMENTS_MAPPING, {
				defaultValue: null,
				validators: [
					(v) => (v === null || '' ? 'A size segment is required' : ''),
				],
			}),
			areas: ListField(StringField(), { minLength: 1 }),
			monthly_budget: NumberField({ required: true, minValue: 0 }),
			company: StringField({ required: true }),
			email: EmailField({ required: true, validators: [isRestrictedDomain] }),
			phone_number: PhoneNumberField({ required: true }),
			city: StringField({
				required: true,
				validators: [(v) => (v === '' ? 'City is required' : '')],
			}),
		},
		// @ts-ignore
		// TODO: need appropriate type handling
		mapping,
		onFormChange: handleFormChange,
		// @ts-ignore
		// TODO: need appropriate type handling
		onFormSubmit: handleFormSubmit,
	})
	const urlGen = useSerpUrlGenerator()

	const city = form.getValue('city')
	const areas = useMemo(() => {
		if (!mappedAreas) {
			return undefined
		}

		/**
		 * Use Set to get unique areas in case someone selects "Innanför tullarna" and areas that are also in "Innanför tullarna"
		 * or multiple areas in Uppsala or Helsingborg where the whole city will always used for filtering.
		 */
		return [
			...new Set(
				form
					.getValue('areas')
					.map((area) => mappedAreas[city]![area])
					.flat() as string[]
			),
		]
	}, [city, form, mappedAreas])

	function handleFormChange(data: FormLeadPayload) {
		if (data.email || data.phone_number) {
			delay(async () => {
				try {
					const result = await createOrUpdateFormLead(leadID, data)
					const newID = result.id
					setLeadID(newID)
				} catch (e) {
					if (e instanceof StatusError) {
						void handleFormLeadError(e)
					}
					throw e
				}
			})
		}
	}

	async function handleFormSubmit(data: Promise<FormLeadPayload>) {
		try {
			const formData = await data
			await createOrUpdateFormLead(leadID, { ...formData, submitted: true })
			const showCalendly =
				formData.size_segment.max && formData.size_segment.max > 2
			const searchParams = new URLSearchParams()
			if (showCalendly) {
				searchParams.set('email', formData.email)
			}
			if (formData.size_segment.min) {
				searchParams.set('min_ws', formData.size_segment.min.toString())
			}
			if (areas) {
				areas.forEach((area) => searchParams.append('area', area))
			}
			searchParams.set('budget', formData.monthly_budget.toString())
			searchParams.set('city', formData.city.toLocaleLowerCase())
			history.push(`${pathname}/success?${searchParams.toString()}`)
			GAAction({
				category: 'engagement',
				action: 'interest_sent',
				label: 'filled_kontorshjalpen',
			})
			sendGA4Event('filled_kontorshjalpen', {
				category: 'lead',
				page: 'kontorshjälpen',
				budget: formData.monthly_budget,
				size: formData.size_segment.min || 'not set',
				city: formData.city,
				value: (formData.monthly_budget * 12) / 100,
				currency: 'sek',
			})
		} catch (e) {
			if (e instanceof StatusError) {
				void handleFormLeadError(e)
			}
			throw e
		}
	}

	async function handleFormLeadError(error: StatusError) {
		// If we received a 400, adds the errors to the form, otherwise a generic message should appear
		let errorMessages = []
		if (!error.responseText) {
			errorMessages.push(error.toString())
		} else {
			switch (error.statusCode) {
				case 400: {
					// eslint-disable-next-line
					const content = (await error.responseJson) as {
						[key: string]: string[]
					}
					form.setErrors(content)
					if (content['non_field_errors']) {
						errorMessages = content['non_field_errors']
					} else if (content['details']) {
						errorMessages.push(content['details'])
					}
					break
				}
				default:
					errorMessages.push(error.toString())
			}
		}
	}

	function isRestrictedDomain(value: string) {
		const restrictedDomains = [
			'gmail.com',
			'hotmail.com',
			'outlook.com',
			'live.se',
			'yahoo.com',
			'icloud.com',
			'hotmail.se',
			'yahoo.se',
		]
		const position = value.indexOf('@')
		if (position) {
			const domain = value.substring(position + 1)
			return restrictedDomains.indexOf(domain.toLowerCase()) < 0
				? ''
				: 'Please enter company email'
		}
		return ''
	}

	const { isStepSubmitted, isStepValid, submitStep, isStepVisible } = useSteps(
		[
			{ name: 'city', fieldnames: ['city'] },
			{ name: 'location', fieldnames: ['areas'] },
			{ name: 'size', fieldnames: ['size_segment'] },
			{ name: 'budget', fieldnames: ['monthly_budget'] },
			{ name: 'company', fieldnames: ['company'] },
			{ name: 'email', fieldnames: ['email'] },
			{ name: 'phone', fieldnames: ['phone_number'] },
		],
		form.state
	)

	function trySubmit(step: string) {
		const res = submitStep(step)
		if (res === true) {
			GAAction({
				category: 'office_help',
				action: 'step_completed',
				label: step,
			})
			sendGA4Event('initiate_kontorshjalpen', { step: step })
		}
	}
	const searchResult = useQuery(
		getCoworkingSearchResultQuery(
			isStepSubmitted('size') &&
				form.getValue('size_segment') == '1-2' &&
				areas != undefined
				? {
						areas: areas,
						area_url: areas.length
							? undefined
							: form.getValue('city').toLowerCase(),
						min_workspaces: 2,
						types: [FILTER_TYPE_OFFICE],
				  }
				: {}
		)
	)

	let visibleErrors = ''
	if (form.getNonFieldErrors().length > 0) {
		visibleErrors = form.getNonFieldErrors().join('\n')
	} else {
		for (const [_key, errors] of Object.entries(form.state.errors)) {
			if (errors.length > 0) {
				visibleErrors += `${errors.join(', ')}\n`
			}
		}
	}
	return (
		<>
			<Conversation>
				<ConversationQuestion
					number={1}
					isValid={isStepValid('city')}
					isVisible={isStepVisible('city')}
					onValidate={() => trySubmit('city')}
					isSubmitted={isStepSubmitted('city')}
					messages={[
						t(
							`Hej! Mitt namn är {{manager_name}} och jag kommer vara er personliga kontorsexpert.`,
							'Till att börja med - I vilken stad söker ni kontor?',
							{ manager_name: manager.name }
						),
					]}
					photo={manager.img}
					input={
						<SelectDropdown
							{...formParams('city', form)}
							placeholder={t('Välj city') || 'Välj stad'}
							options={[
								{ display: t('Välj city'), value: '' },
								...CITY_CHOICES,
							]}
							onBlur={(_) => trySubmit('city')}
						/>
					}
					answerPreText={t('Vi söker kontor i')}
					scrollToTop
				/>
				<ConversationQuestion
					number={2}
					isValid={isStepValid('location')}
					isVisible={isStepVisible('location')}
					onValidate={() => trySubmit('location')}
					isSubmitted={isStepSubmitted('location')}
					messages={[
						t(
							'Om vi pratar läge — Vilket eller vilka områden är ni intresserade av?'
						),
					]}
					photo={manager.img}
					input={
						<MultiChoiceDisplayValue
							{...formParams('areas', form)}
							placeholder={t('Välj område') || 'Välj område'}
							options={
								form.getValue('city')
									? AREA_CHOICES[form.getValue('city')]!
									: []
							}
							onBlur={(_) => trySubmit('location')}
						/>
					}
					answerPreText={t('Gärna inom')}
					afterAnswer={
						<InlineMultiChoice
							{...formParams('areas', form)}
							options={
								form.getValue('city')
									? AREA_CHOICES[form.getValue('city')]!
									: []
							}
						/>
					}
					scrollToTop
				/>
				<ConversationQuestion
					number={3}
					isValid={isStepValid('size')}
					isVisible={isStepVisible('size')}
					onValidate={() => {
						trySubmit('size')
						if (form.getValue('size_segment') == '1-2') {
							sendGA4Event('small_kontorshjalpen', { step: 'chose_small' })
						}
					}}
					isSubmitted={isStepSubmitted('size')}
					messages={[t('Hur många arbetsplatser behöver ni?')]}
					photo={manager.img}
					input={
						<SelectDropdown
							{...formParams('size_segment', form)}
							placeholder={t('Välj storlek') || 'Välj storlek'}
							options={[
								{ display: t('Välj storlek'), value: '' },
								...SIZE_SEGMENT_CHOICES,
							]}
							onBlur={(_) => trySubmit('size')}
						/>
					}
					answerPreText={t('Vi söker kontor för ca.')}
					answerPostText={t(ANSWER_POST_TEXTS['size']) || 'arbetsplatser'}
					scrollToTop
				/>
				{isStepSubmitted('size') ? (
					searchResult.isLoading ? (
						<></>
					) : searchResult.data && areas ? (
						<Fade in={true}>
							<MinWorkspacesSection
								linkToMarketplace={urlGen({
									minWorkspaces: 2,
									maxWorkspaces: null,
									maxBudget: null,
									type: OFFICE_TYPE,
									services: [],
									sorting: SORT_RECOMMENDED,
									areaCodes: areas,
									filterType: [FILTER_TYPE_OFFICE],
								})}
								locationsLength={searchResult.data.locations.length}
							/>
						</Fade>
					) : (
						<>
							<ConversationQuestion
								number={4}
								isValid={isStepValid('budget')}
								isVisible={isStepVisible('budget')}
								onValidate={() => trySubmit('budget')}
								isSubmitted={isStepSubmitted('budget')}
								messages={[
									t('Vad har ni för månadsbudget för ett nytt kontor?'),
								]}
								photo={manager.img}
								input={
									<RoundedTextInput
										{...formParams('monthly_budget', form)}
										placeholder="50 000"
										filled={isStepSubmitted('budget')}
										onKeyPress={(e) => e.key === 'Enter' && trySubmit('budget')}
										onBlur={(_) => trySubmit('budget')}
										style={{ maxWidth: '100%' }}
									/>
								}
								answerPreText={t('Vi kan som mest betala')}
								answerPostText={
									t(ANSWER_POST_TEXTS['budget']) || 'SEK/månad för vårt kontor'
								}
								scrollToTop
							/>
							<ConversationQuestion
								number={5}
								isValid={isStepValid('company')}
								isVisible={isStepVisible('company')}
								onValidate={() => trySubmit('company')}
								isSubmitted={isStepSubmitted('company')}
								messages={[t('Vad heter ert bolag?')]}
								photo={manager.img}
								input={
									<RoundedTextInput
										{...formParams('company', form)}
										placeholder="Exempelbolag AB"
										filled={isStepSubmitted('company')}
										onKeyPress={(e) =>
											e.key === 'Enter' && trySubmit('company')
										}
										onBlur={(_) => trySubmit('company')}
										style={{ maxWidth: '100%' }}
									/>
								}
								answerPreText={t('Vårt bolag heter')}
								scrollToTop
							/>
							<ConversationQuestion
								number={6}
								isValid={isStepValid('email')}
								isVisible={isStepVisible('email')}
								onValidate={() => trySubmit('email')}
								isSubmitted={isStepSubmitted('email')}
								messages={[
									t(
										'För att kunna skicka förslag så behöver jag er epostadress.'
									),
								]}
								photo={manager.img}
								input={
									<RoundedTextInput
										{...formParams('email', form)}
										filled={isStepSubmitted('email')}
										placeholder="ditt.namn@bolag.se"
										onKeyPress={(e) => e.key === 'Enter' && trySubmit('email')}
										onBlur={(_) => trySubmit('email')}
										style={{ maxWidth: '100%' }}
									/>
								}
								answerPreText={t('Följande e-mail går att nå oss på')}
								afterAnswer={t('(Obs! Vi respekterar er integritet.)')}
								scrollToTop
							/>
							<ConversationQuestion
								number={7}
								isValid={isStepValid('phone')}
								isVisible={isStepVisible('phone')}
								onValidate={() => trySubmit('phone')}
								isSubmitted={isStepSubmitted('phone')}
								messages={[
									t(
										'För att ta fram de mest relevanta förslagen kan jag behöva ställa uppföljande frågor, vilket nummer når jag er på då?'
									),
								]}
								photo={manager.img}
								input={
									<RoundedTextInput
										{...formParams('phone_number', form)}
										filled={isStepSubmitted('phone')}
										placeholder="073-XXX-XXX"
										onKeyPress={(e) => e.key === 'Enter' && trySubmit('phone')}
										onBlur={(_) => trySubmit('phone')}
										style={{ maxWidth: '100%' }}
									/>
								}
								answerPreText={t('Vi kan nås på följande nummer')}
								scrollToTop
							/>
						</>
					)
				) : (
					<></>
				)}
			</Conversation>
			<div
				className={clsx('submit-box', {
					hidden: !isStepSubmitted('phone'),
				})}
			>
				<img src={Shape} />
				<ErrorText>{visibleErrors}</ErrorText>
				<PrimaryButton
					action={(_) => form.trySubmit()}
					className="submit-button"
					disabled={
						!isStepValid('email') ||
						!isStepValid('phone') ||
						!isStepValid('location') ||
						!isStepValid('size') ||
						!isStepValid('budget') ||
						!isStepValid('company')
					}
				>
					{t('Bekräfta mina uppgifter')}
				</PrimaryButton>
			</div>
		</>
	)
}

export { FormSection }
